import {
    Popover,
    Box,
    Stack,
    Button,
    Typography,
    Checkbox,
    FormControlLabel,
    Divider,
} from '@mui/material';
import { Organization } from 'src/Constants/Types';
import { determineOwnerStatus, SandboxFilters } from 'src/Pages/Admin/Sandboxes/SandboxesAdminPage';

type Props = {
    anchorEl: HTMLElement | null;
    open: boolean;
    filters: SandboxFilters;
    onChange: (filters: SandboxFilters) => void;
    onClose: () => void;
    sandboxes: any[]; // Array of sandboxes to extract filter options from
    organizations: Organization[];
};

export const SandboxFiltersPopover = ({
    anchorEl,
    open,
    filters,
    onChange,
    onClose,
    sandboxes,
    organizations,
}: Props) => {
    // Get unique organizations that exist in the sandbox data
    const availableOrganizations = organizations.filter(org =>
        sandboxes.some(sandbox => sandbox.organizationId === org.id)
    );

    const getSandboxesExcludingFilter = (excludeFilterType: 'organization' | 'role' | 'status') => {
        return sandboxes.filter(sandbox => {
            // Apply all filters except the specified one
            if (excludeFilterType !== 'organization' && filters.organizationIds && filters.organizationIds.length > 0) {
                if (!filters.organizationIds.includes(sandbox.organizationId)) return false;
            }
            return true;
        });
    };

    const getOrganizationCount = (orgId: string): number => {
        const availableSandboxes = getSandboxesExcludingFilter('organization');
        return availableSandboxes.filter(sandbox => sandbox.organizationId === orgId).length;
    };

    const getSharedStatusCount = (status: boolean): number => {
        const availableSandboxes = getSandboxesExcludingFilter('status');
        return availableSandboxes.filter(sandbox => sandbox.isShared === status).length;
    }

    const getOwnerStatusCount = (status: string): number => {
        const availableSandboxes = getSandboxesExcludingFilter('status');
        return availableSandboxes.filter(sandbox => determineOwnerStatus(sandbox) === status).length;
    }

    const handleOrganizationChange = (orgId: string, checked: boolean) => {
        const current = filters.organizationIds || [];
        const updated = checked
            ? [...current, orgId]
            : current.filter(id => id !== orgId);

        onChange({
            ...filters,
            organizationIds: updated.length > 0 ? updated : undefined
        });
    };

    const handleSharedStatusChange = (status: boolean, checked: boolean) => {
        const current = filters.isShared || [];
        const updated = checked
            ? [...current, status]
            : current.filter(s => s !== status);

        onChange({
            ...filters,
            isShared: updated.length > 0 ? updated : undefined
        });
    }

    const handleOwnerStatusChange = (status: string, checked: boolean) => {
        const current = filters.ownerStatuses || [];
        const updated = checked
            ? [...current, status]
            : current.filter(s => s !== status);

        onChange({
            ...filters,
            ownerStatuses: updated.length > 0 ? updated : undefined
        });
    }

    const clearFilters = () => onChange({});

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <Box sx={{ p: 2, width: 320, maxHeight: 400, overflow: 'auto' }}>
                <Stack spacing={2}>
                    {/* Organizations */}
                    {availableOrganizations.length > 0 && (
                        <>
                            <Typography variant="subtitle2" fontWeight="bold">
                                Organizations
                            </Typography>
                            <Stack spacing={1} sx={{ maxHeight: 120, overflow: 'auto' }}>
                                {availableOrganizations.map((org) => {
                                    const count = getOrganizationCount(org.id);
                                    return (
                                        <FormControlLabel
                                            key={org.id}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={filters.organizationIds?.includes(org.id) || false}
                                                    onChange={(e) => handleOrganizationChange(org.id, e.target.checked)}
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                    <span>{org.name}</span>
                                                    <Typography variant="caption" color="text.secondary">
                                                        ({count})
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    );
                                })}
                            </Stack>
                            <Divider />
                        </>
                    )}
                    <>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Shared Status
                        </Typography>
                        <Stack spacing={1} sx={{ maxHeight: 120, overflow: 'auto' }}>
                            {[true, false].map((status) => {
                                const count = getSharedStatusCount(status);
                                return (
                                    <FormControlLabel
                                        key={status.toString()}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={filters.isShared?.includes(status) || false}
                                                onChange={(e) => handleSharedStatusChange(status, e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span>{status ? 'Shared' : 'Not Shared'}</span>
                                                <Typography variant="caption" color="text.secondary">
                                                    ({count})
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                );
                            })}
                        </Stack>
                        <Divider />
                    </>
                    <>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Owner Status
                        </Typography>
                        <Stack spacing={1} sx={{ maxHeight: 150, overflow: 'auto' }}>
                            {['Enabled', 'Disabled', 'Deleted'].map((status) => {
                                const count = getOwnerStatusCount(status);
                                if (count === 0) return null;
                                return (
                                    <FormControlLabel
                                        key={status.toString()}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={filters.ownerStatuses?.includes(status) || false}
                                                onChange={(e) => handleOwnerStatusChange(status, e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span>{status}</span>
                                                <Typography variant="caption" color="text.secondary">
                                                    ({count})
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                );
                            })}
                        </Stack>
                        <Divider />
                    </>

                    {/* Actions */}
                    <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                        <Button onClick={clearFilters}>Clear All</Button>
                        <Button variant="contained" onClick={onClose}>
                            Apply
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </Popover>
    );
};
